<template>
    <div class="manage-keys-container">
        <div class="header">
            <h2>Gerenciar Chaves</h2>
            <div class="actions">
                <Button :disabled="fetchKeys.isLoading" tipo="second" icon="mdi-refresh" label="Atualizar" @ClickEvent="refreshKeys" />
                <Button tipo="primary" icon="mdi-plus" label="Nova chave" @ClickEvent="openCreateKeyDialog" />
            </div>
        </div>

        <div v-if="fetchKeys.isLoading" class="fetch-loading-state">
            <v-progress-circular indeterminate color="#0f8fef"></v-progress-circular>
        </div>

        <p v-else-if="fetchKeys.error">{{ fetchKeys.error }}</p>

        <div v-else-if="fetchKeys.keys.length < 1" class="empty-state">
            <i class="mdi mdi-key-outline"></i>
            <p>Nenhuma chave cadastrada</p>
        </div>

        <div v-else class="keys-list">
            <div class="key-card" v-for="key in fetchKeys.keys" :key="key.id">
                <v-menu bottom left :close-on-click="true">
                    <template v-slot:activator="{ on, attrs }">
                        <button class="menu-button" v-bind="attrs" v-on="on">
                            <i class="mdi mdi-dots-vertical"></i>
                        </button>
                    </template>

                    <v-list>
                        <v-list-item @click="handleMenuAction('edit', key)">
                            <v-list-item-title>Editar</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="handleMenuAction('delete', key)">
                            <v-list-item-title>Excluir</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <div class="card-body">
                    <div class="key-info key-info-full key-header-row">
                        <div class="key-label-status">
                            <label>KEY:</label>
                            <span class="key-status" :class="{ active: key.active, inactive: !key.active }">
                                {{ key.active ? 'Ativo' : 'Inativo' }}
                            </span>
                        </div>
                        <span class="key-value">{{ key.key || "Sua chave está sendo gerada, aguarde alguns segundos e atualize a lista. Se o problema persistir, contate o suporte." }}</span>
                    </div>

                    <div class="key-info-grid">
                        <div class="key-info">
                            <label>Criado em:</label>
                            <span>{{ formatDate(key.created_at) }}</span>
                        </div>

                        <div class="key-info">
                            <label>Criado por:</label>
                            <span>{{ key.created_by }}</span>
                        </div>

                        <div class="key-info">
                            <label>Limite de Uso:</label>
                            <span>{{ key.usageLimit }}</span>
                        </div>

                        <div class="key-info" v-if="key.expires_at">
                            <label>Expira em:</label>
                            <span :class="{ expired: isExpired(key.expires_at) }">
                                {{ formatDate(key.expires_at) }}
                            </span>
                        </div>
                    </div>

                    <div class="key-info key-info-full" v-if="key.description">
                        <label>Descrição:</label>
                        <span>{{ key.description }}</span>
                    </div>

                    <div class="key-usage" v-if="key.usage && key.usage.length > 0">

                        <v-expansion-panels>
                            <v-expansion-panel>
                                <v-expansion-panel-header>
                                    <label style="cursor: pointer;">Uso ({{ key.usage.length }}/{{ key.usageLimit
                                    }}):</label>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="usage-list-horizontal">
                                        <div v-for="(usage, index) in key.usage" :key="index" class="usage-item">
                                            <span>{{ formatDate(usage.date) }}</span>
                                            <span class="install-id">{{ usage.installId }}</span>
                                        </div>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>
                </div>

            </div>
        </div>

        <!--Edit key dialog-->
        <v-dialog v-model="editKey.isDialogOpen" width="500">
            <v-card>

                <v-card-title class="text-h5 grey lighten-2">
                    Editar Chave
                </v-card-title>

                <div v-if="editKey.selectedKey">

                    <v-card-text>
                        <p><strong>KEY:</strong> {{ editKey.selectedKey.key }}</p>
                        <p><strong>Criado em:</strong> {{ formatDate(editKey.selectedKey.created_at) }}</p>
                        <p><strong>Criado por:</strong> {{ editKey.selectedKey.created_by }}</p>
                        <!-- Adicione aqui os campos de edição -->
                    </v-card-text>
                    <TextField label="Descrição" placeholder="Adicionar descrição (opcional)" type="String"
                        :model="editKey.form.description" @input="editKey.form.description = $event"></TextField>
                    <TextField label="Limite de uso" type="number" :model="editKey.form.usageLimit"
                        @input="editKey.form.usageLimit = $event">
                    </TextField>
                    <v-card-text>
                        <v-switch inset v-model="editKey.form.active"
                            :label="editKey.form.active ? 'Chave Ativa' : 'Chave Inativa'"></v-switch>

                        <p class="error-text" v-if="editKey.error">{{ editKey.error }}</p>
                    </v-card-text>
                </div>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-progress-circular v-if="editKey.isLoading" indeterminate color="#0f8fef"></v-progress-circular>
                    <div v-else>
                        <v-btn color="grey" text @click="editKey.isDialogOpen = false">
                            Cancelar
                        </v-btn>
                        <v-btn color="primary" text @click="updateKey">
                            Salvar
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!--Create key dialog-->
        <v-dialog v-model="createKey.isDialogOpen" width="500">
            <v-card>

                <v-card-title class="text-h5 grey lighten-2">
                    Criar Chave
                </v-card-title>

                <v-card-text>
                    <TextField label="Descrição" placeholder="Adicionar descrição (opcional)" type="String"
                        :model="createKey.form.description" @input="createKey.form.description = $event"></TextField>
                    <TextField :model="createKey.form.usageLimit" label="Limite de uso"
                        @input="createKey.form.usageLimit = $event" type="number"> </TextField>

                    <p class="error-text" v-if="createKey.error">{{ createKey.error }}</p>
                </v-card-text>
                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-progress-circular v-if="createKey.isLoading" indeterminate color="#0f8fef"></v-progress-circular>
                    <div v-else>
                        <v-btn color="grey" text @click="createKey.isDialogOpen = false">
                            Cancelar
                        </v-btn>
                        <v-btn color="primary" text @click="saveKey">
                            Salvar
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import TextField from '../../Inputs/TextField.vue';
import { useAssetsKeyService } from '../../../Services/assetsKeyService'

const { createKey, editKey, fetchKeys, deleteKey } = useAssetsKeyService();
const DEFAULT_USAGE_LIMIT = 10;

export default {
    name: "ManageKeys",
    data() {
        return {
            sortAscending: true,
            fetchKeys: {
                isLoading: false,
                error: null,
                keys: [],
            },
            createKey: {
                form: {
                    description: '',
                    usageLimit: DEFAULT_USAGE_LIMIT,
                },
                isDialogOpen: false,
                isLoading: false,
                error: null,
            },
            editKey: {
                selectedKey: null,
                form: {
                    description: '',
                    usageLimit: 0,
                    active: false
                },
                isDialogOpen: false,
                isLoading: false,
                error: null,
            },
        };
    },
    components: {
        TextField
    },
    methods: {
        async getItems() {

            this.fetchKeys.isLoading = true;
            this.fetchKeys.error = null;
            this.fetchKeys.keys = [];

            const res = await fetchKeys();
            this.fetchKeys.isLoading = false;

            if (res.error) {
                this.fetchKeys.error = res.error;
            } else {
                this.fetchKeys.keys = res.keys ? res.keys : [];
            }
        },
        openCreateKeyDialog() {
            this.createKey.isDialogOpen = true;
            this.createKey.form.description = '';
            this.createKey.form.usageLimit = DEFAULT_USAGE_LIMIT;
        },
        refreshKeys() {
            this.getItems();
        },
        handleMenuAction(action, key) {
            this.editKey.selectedKey = key;
            if (action === 'edit') {
                this.editKey.form.description = key.description;
                this.editKey.form.usageLimit = key.usageLimit;
                this.editKey.form.active = key.active;
                this.editKey.created_by = key.created_by;
                this.editKey.error = null;
                this.editKey.isDialogOpen = true;


            } else if (action === 'delete') {
                this.deleteKey(key.id);
            }
        },
        async saveKey() {
            this.createKey.isLoading = true;
            this.createKey.error = null;
            const res = await createKey(this.createKey.form.description, this.createKey.form.usageLimit)
            this.createKey.isLoading = false;

            if (res.error) {
                this.createKey.error = res.error;
                return;
            }

            this.createKey.isDialogOpen = false;
            this.createKey.error = null;
            window.alert("Chave criada com sucesso!");
            this.getItems();

        },
        async updateKey() {
            this.editKey.isLoading = true;
            this.editKey.error = null;

            const errorMessage = await editKey(this.editKey.selectedKey.id, this.editKey.form.description, this.editKey.form.usageLimit, this.editKey.form.active)

            this.editKey.isLoading = false;

            if (errorMessage) {
                this.editKey.error = errorMessage;
                return;
            }

            if (!errorMessage) {
                window.alert("Chave atualizada com sucesso!");
                this.editKey.isDialogOpen = false;
                this.getItems();
                return;
            }
        },
        async deleteKey(id) {
            if (window.prompt("Meu Deus! 😱 Não tem volta!!! Digite 'EXCLUIR' para confirmar a exclusão desta chave") === 'EXCLUIR') {
                await deleteKey(id);
                this.getItems();
            } else {
                window.alert("Exclusão cancelada");
            }
        },
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('pt-BR');
        },
        isExpired(date) {
            return new Date(date) < new Date();
        }
    },
    mounted() {
        this.getItems();
    },
}
</script>

<style scoped>
.manage-keys-container {
    padding: 20px;
    width: 100%;
    margin: 0 auto;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.header h2 {
    margin: 0;
    color: #333;
}

.actions {
    display: flex;
    gap: 10px;
}

.btn-sort,
.btn-create,
.btn-edit,
.btn-delete {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    transition: all 0.3s;
}

.btn-sort {
    background-color: #f5f5f5;
    color: #333;
}

.btn-sort:hover {
    background-color: #e0e0e0;
}

.btn-create {
    background-color: #1976d2;
    color: white;
}

.btn-create:hover {
    background-color: #1565c0;
}

.keys-list {
    display: grid;
    gap: 20px;
}

.key-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
}

.menu-button {
    width: 40px;
    height: 40px;
    position: absolute;
    top: 12px;
    right: 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: all 0.3s;
}

.menu-button:hover {
    background-color: #f5f5f5;
    color: #333;
}

.menu-button i {
    font-size: 20px;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.key-header-row {
    padding-bottom: 12px;
    border-bottom: 1px solid #e0e0e0;
    ;
}

.key-label-status {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.key-status {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

.key-status.active {
    background-color: #b9f7bb;
    color: #105512;
}

.key-status.inactive {
    background-color: #f5aba6;
    color: #611510;
}

.key-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.key-info-full {
    grid-column: 1 / -1;
}

.key-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.key-info label {
    font-size: 12px;
    color: #666;
    font-weight: 600;
}

.key-info span {
    color: #333;
}

.key-value {
    font-family: monospace;
    background-color: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
    word-break: break-all;
}

.key-usage {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.key-usage label {
    font-size: 12px;
    color: #666;
    font-weight: 600;
}

.usage-list-horizontal {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.usage-item {
    background-color: #f5f5f5;
    padding: 6px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.usage-item span {
    white-space: nowrap;
}

.usage-item .install-id {
    color: #1976d2;
    font-weight: 600;
}

.expired {
    color: #f44336;
    font-weight: bold;
}

.card-footer {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e0e0e0;
}

.btn-edit {
    background-color: transparent;
    color: #ff9800;
    border: 1px solid #ff9800;
    width: 100%;
}

.btn-edit:hover {
    background-color: #fff3e0;
}

.btn-delete {
    background-color: #f44336;
    color: white;
    flex: 1;
}

.btn-delete:hover {
    background-color: #d32f2f;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #666;
}

.empty-state i {
    font-size: 64px;
    color: #ccc;
    margin-bottom: 20px;
}

.empty-state p {
    font-size: 18px;
    margin-bottom: 20px;
}

.fetch-loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px 20px;
}

.highlighed-text {
    font-weight: bolder;
    color: #1976d2;
}

.error-text {
    color: #e0493e;
    font-weight: 600;
}

/* Menu styles */
::v-deep .v-list-item {
    cursor: pointer;
    transition: background-color 0.3s;
}

::v-deep .v-list-item:hover {
    background-color: #f5f5f5;
}
</style>